import { DEFAULTS } from './product/CONSTANTS'

import axiosGetFullProductJSON from './product/axiosGetFullProductJSON'
import mapOptionsByImageIndex from './product/mapOptionsByImageIndex'
import getVariantImageUrl from './product/getVariantImageUrl'
import getCurrentSelectedOptionsFromFields from './product/getCurrentSelectedOptionsFromFields'
import updatePrice from './product/updatePrice'
import updateVariantFormField from './product/updateVariantFormField'
import switchStockDisplay from './product/switchStockDisplay'
import getVariantFromVariantsAndOptions from './product/getVariantFromVariantsAndOptions'
import updateVariantUrl from './product/updateVariantUrl'
import setActiveThumbnail from './product/setActiveThumbnail'

import getMatchingOptionsFromImageIndex from './product/getMatchingOptionsFromImageIndex'
import getClosestOptionsToCurrentlySelected from './product/getClosestOptionsToCurrentlySelected'
import getVariantById from './product/getVariantById'
import updateSelects from './product/updateSelects'
import getThumbnailPosition from './product/getThumbnailPosition'

import { ProductVariant, Product } from '../../app-layer/util/typings'
import safeJSONParse from '../utils/safeJsonParse'
import initQuantitySelector from './cart/quantitySelector'

export type ProductOptionFields =
  | NodeListOf<HTMLSelectElement | HTMLInputElement>
  | HTMLInputElement[]
  | HTMLSelectElement[]

export const productInit = async (
  options: typeof DEFAULTS,
  productUpdatedCallBack = (variant: ProductVariant) => variant
): Promise<void> => {
  const opts = { ...DEFAULTS, options }

  const productForm = document.querySelector(
    opts.PRODUCT_FORM_SELECTOR
  ) as HTMLFormElement

  if (!productForm) {
    return
  }

  //get the data
  const productData: Product | void = await axiosGetFullProductJSON(
    productForm.dataset.productForm
  )

  if (!productData) {
    return
  }

  const optionsByImageIndex = mapOptionsByImageIndex(productData.variants)

  //get all the elements
  const variantIdFormField = document.querySelector(
    opts.PRODUCT_VARIANT_ID_FORM_FIELD
  ) as HTMLInputElement
  const atcButton: HTMLButtonElement = document.querySelector(
    opts.PRODUCT_FORM_SUBMIT_BUTTON_SELECTOR
  )
  const priceEls = document.querySelectorAll(opts.PRODUCT_PRICE_SELECTOR)
  const compareAtpriceEls = document.querySelectorAll(
    opts.PRODUCT_COMPARE_AT_PRICE_SELECTOR
  )
  const optionFields = document.querySelectorAll(
    opts.PRODUCT_OPTION_FIELD_SELECTOR
  ) as NodeListOf<HTMLSelectElement | HTMLInputElement>
  const imageThumbs = document.querySelectorAll(
    opts.PRODUCT_IMAGE_THUMBS_SELECTOR
  )
  const inStockLabel: HTMLSpanElement = document.querySelector(opts.PRODUCT_IN_STOCK_LABEL)
  const stockLeftLabel : HTMLSpanElement = document.querySelector(opts.PRODUCT_STOCK_LEFT_LABEL)
  const oosStockLabel: HTMLSpanElement = document.querySelector(opts.PRODUCT_OOS_LABEL)
  const unavailableStockLabel: HTMLSpanElement = document.querySelector(opts.PRODUCT_UNAVAILABLE_LABEL)
  const variantSKU: HTMLParagraphElement = document.querySelector(opts.VARIANT_SKU)
  const productOptionName: NodeListOf<HTMLSpanElement> = document.querySelectorAll(opts.PRODUCT_OPTION_NAME)
  const restockDates: NodeListOf<HTMLSpanElement> = document.querySelectorAll(opts.PRODUCT_EST_RESTOCK_DATE)

  const extraLabels = {
    inStockLabel,
    stockLeftLabel,
    oosStockLabel,
    unavailableStockLabel,
    variantSKU,
    productOptionName,
    restockDates
  }

  const stockMap: Record<string, string> = (() => {
    try {
      return JSON.parse(productForm.getAttribute(opts.PRODUCT_STOCK_MAP))
    } catch (e) {
      console.info('No stockmap on product form')
      return {}
    }
  })()

  const updateProductPrice = updatePrice(
    priceEls,
    compareAtpriceEls,
    window['theme'].moneyFormat
  )
  const updateProductVariantFormField = updateVariantFormField(
    variantIdFormField,
    productData.handle
  )

  const switchProductStockDisplay = switchStockDisplay({
    stockWrapper: productForm,
    stockWrapperAttr: opts.PRODUCT_STOCK_WRAPPER_ATTR,
    atcButton,
    oosAttr: opts.PRODUCT_OOS_TEXT_ATTR,
    inStockAttr: opts.PRODUCT_IN_STOCK_TEXT_ATTR,
    unavailableAttr: opts.PRODUCT_UNAVAILABLE_TEXT_ATTR,
    preOrderAttr: opts.PRODUCT_PRE_ORDER_ATTR,
  })

  const getAvailabilityFlag = (variant?: ProductVariant) => {
    return (variant || optionFields.length === 0) ? stockMap[variant?.id.toString()] : 'unavailable'
  }


  const updateStockDisplay = (variant?: ProductVariant) => {
    const flag = getAvailabilityFlag(variant)
    switchProductStockDisplay(flag, variant?.price)
  }

  const updateVariantData = (variant?: ProductVariant) => {
    updateStockDisplay(variant)
    if (variant) {
      updateVariantUrl(variant)
      updateProductPrice(variant)
      updateProductVariantFormField(variant)
      productUpdatedCallBack(variant)
    }
    if (!variant && productData.variants.length === 1) {
      const variant = productData.variants[0] 
      updateStockDisplay(variant)
      updateVariantUrl(variant)
      updateProductPrice(variant)
      updateProductVariantFormField(variant)
      productUpdatedCallBack(variant)
    }
  }

  const updateProductToMatchThumbnail = (position: number) => {
    const matchingOptions = getMatchingOptionsFromImageIndex(
      optionsByImageIndex,
      position
    )
    const currentlySelectedOptions =
      getCurrentSelectedOptionsFromFields(optionFields)
    const closest = getClosestOptionsToCurrentlySelected(
      currentlySelectedOptions,
      matchingOptions
    )
    if (!closest) {
      return
    }
    const { id, options } = closest
    const variant = getVariantById(productData.variants, id)
    updateSelects(optionFields, options)
    updateVariantData(variant)
  }
  // important 
  const onFieldsChange = (fields: ProductOptionFields) => {
    const currentOptions = getCurrentSelectedOptionsFromFields(fields)
    const variant = getVariantFromVariantsAndOptions(
      productData.variants,
      currentOptions
    )
    updateVariantData(variant)
    if (!variant) {
      return
    }
    const imageUrl = getVariantImageUrl(variant)
    if (!imageUrl) {
      return
    }
  }

  const onProductThumbnailClick = (thumbnail: Element) => () => {
    const position = getThumbnailPosition(
      opts.PRODUCT_IMAGE_THUMBS_ATTR,
      thumbnail
    )
    setActiveThumbnail(
      imageThumbs,
      opts.PRODUCT_IMAGE_THUMBS_ATTR,
      opts.PRODUCT_THUMBNAIL_ACTIVE_CLASS,
      position
    )
    //Don't do anything if element contains a video
    if (!((thumbnail as HTMLLIElement).dataset.media === 'video')) {
      if (opts.SWITCH_OPTIONS_ON_THUMBNAIL_SELECTION) {
        updateProductToMatchThumbnail(position)
      }
    }
  }

  imageThumbs.forEach((thumbnail) =>
    thumbnail.addEventListener('click', onProductThumbnailClick(thumbnail))
  )

  const buildToolTip = (wrapper: HTMLSpanElement, toolTipContent: boolean | any[] | Record<string, unknown>, str: string) => {
    const parent = document.createElement('div')
    parent.classList.add('tooltip')
    const icon = document.createElement('span');
    icon.classList.add('tooltip-icon');
    parent.appendChild(icon)
    const content = document.createElement('span');
    content.classList.add('tooltip-content')
    content.innerText = toolTipContent[str];
    parent.appendChild(content)
    wrapper.appendChild(parent)
  }

  //Find the fields that are checked or selected and push them into the fields array at the correct position
  const fields: HTMLInputElement[] | HTMLSelectElement[] = [];
  [...optionFields]
    .filter((field) => {
      if ((field as HTMLInputElement).localName === 'input') {
        return (field as HTMLInputElement).checked
      } else if ((field as HTMLSelectElement).localName === 'select') {
        return [...(field as HTMLSelectElement).selectedOptions].some(
          // this might need updated to get all fields??
          (option) => option.selected
        )
      }
      return null
    })
    .forEach((field) => {
      fields[parseInt(field.dataset.position)] = field
    })

  const updateVariantClasses = () => {
    optionFields.forEach((select) => {
      const currentOptions = getCurrentSelectedOptionsFromFields(fields)
      currentOptions[parseInt(select.dataset.position)] = select.value

      const variant = getVariantFromVariantsAndOptions(
        productData.variants,
        currentOptions
      )
      if (select.tagName.toLowerCase() === 'input') {
        if (parseInt(select.dataset.position) !== 0 && parseInt(select.dataset.position) !== 1) {
          if (getAvailabilityFlag(variant) === 'oos') {
            select.parentElement.classList.add('disabled')
          } else {
            select.parentElement.classList.remove('disabled')
          }

          if (getAvailabilityFlag(variant) === 'unavailable') {
            select.parentElement.classList.add('hide')
          } else {
            select.parentElement.classList.remove('hide')
          }
        }
      }

      // Replicate functionality for select dropdowns
      if (select.tagName.toLowerCase() === 'select') {
        if (select instanceof HTMLSelectElement) {
          [...select.options].forEach((option) => {
            currentOptions[parseInt(select.dataset.position)] = option.value
            const variant = getVariantFromVariantsAndOptions(
              productData.variants,
              currentOptions
            )

            if (getAvailabilityFlag(variant) === 'unavailable') {
              option.classList.add('hide') 
              option.setAttribute('disabled', 'true');
            } else {
              option.classList.remove('hide')
              option.removeAttribute('disabled');
            }
          })
        }
      }
    })
  }

  
  const fieldsChange2 = (target, inputPosition) => {
    // Blame will for this...

    fields[inputPosition] = target
    onFieldsChange(fields);

    //Current variant
    const currentOptions = getCurrentSelectedOptionsFromFields(fields)
    const variant = getVariantFromVariantsAndOptions(
      productData.variants,
      currentOptions
    )

    const showVariantRestockDate = (variantId: number): void => {
      [...extraLabels.restockDates].find(date => parseInt(date.dataset.restockId) === variantId)?.classList.remove('hide')
    }

    //Update Option Name
    extraLabels.productOptionName[inputPosition].innerText = target.value
    //Add tooltip if one present.
    const toolTipContent = safeJSONParse((document.querySelector('[data-tooltip-content]') as HTMLDivElement).dataset.tooltipContent);

    if(toolTipContent && toolTipContent[target.value]) {
      buildToolTip(extraLabels.productOptionName[inputPosition], toolTipContent, target.value)
    }

    //Update SKU
    extraLabels.variantSKU.innerText = variant && variant.sku ? variant.sku : ''

    //Deal with the oos items, if any.
    //First remove the disabled class from all of the option fields
    optionFields.forEach((option) =>{
      option.parentElement.classList.remove('disabled');
    })

    // pulls the variant quantities from the DOM
    const variantQuantities = JSON.parse(document.getElementById('variantQuantities').innerHTML) ?? null;

    //Deal with its oos options and stock label
    if (!variant || !variant.available) {
      // fields.forEach((field: HTMLSelectElement | HTMLInputElement) =>
      //   field.parentElement.classList.add('disabled')
      // )
        extraLabels.inStockLabel?.classList.add('hide')
        extraLabels.stockLeftLabel?.classList.add('hide');
        extraLabels.oosStockLabel?.classList.remove('hide');
        extraLabels.unavailableStockLabel?.classList.add('hide');
        if (variant){showVariantRestockDate(variant.id)}
    } else {
      extraLabels.inStockLabel?.classList.remove('hide');
      extraLabels.stockLeftLabel.classList.add('hide');
      extraLabels.oosStockLabel?.classList.add('hide');
      extraLabels.unavailableStockLabel?.classList.add('hide');
      extraLabels.restockDates.forEach(label => label.classList.add('hide'))
      //We want to show the dates if they are pre-order
      if(productData.tags.includes(atcButton.dataset.preorderTag)) {
        [...extraLabels.restockDates].find(date => parseInt(date.dataset.restockId) === variant.id)?.classList.remove('hide')
        showVariantRestockDate(variant.id)
      }

      if (variantQuantities) {
        variantQuantities.variants.forEach((variantAmount) => {
          if (variantAmount.id === variant.id && (variantAmount.inventory_quantity <= 10 && variantAmount.inventory_quantity > 0)) {
            extraLabels.stockLeftLabel?.classList.remove('hide');
            extraLabels.inStockLabel?.classList.add('hide');
            extraLabels.oosStockLabel?.classList.add('hide');

            extraLabels.stockLeftLabel.innerHTML = `
              <span class="indicator indicator--amber"></span>
              Only ${variantAmount.inventory_quantity} left
            `;
          }
        });
      }
    }
    if(!variant) {
      extraLabels.inStockLabel?.classList.add('hide')
      extraLabels.oosStockLabel?.classList.add('hide');
      extraLabels.unavailableStockLabel?.classList.remove('hide');
    }

    //Now for the other options
    // if (productData.options.length === 2) {
    //   const otherVariants = productData.variants.filter(
    //     (child) => {
    //       if(child && variant) {
    //         child.option1 === variant.option1 &&
    //           child.option2 !== variant.option2
    //       }
    //     }
    //   )

    //   if (otherVariants.length) {
    //       [...optionFields]
    //         .filter((option) =>
    //           otherVariants.find(
    //             (other) => other.option2 === option.value && !other.available
    //           )
    //         )
    //         ?.forEach((el) => el.parentElement.classList.add('disabled'))
    //   }
    // }
    // if (productData.options.length === 3) {
    //   const otherVariants = productData.variants.filter(
    //     (child) => {
    //       if(child && variant) {
    //         child.option1 === variant.option1 &&
    //           (child.option2 === variant.option2) &&
    //           child.option3 !== variant.option3
    //       }
    //     }
    //   )
    //   if (otherVariants.length) {
    //       [...optionFields]
    //         .filter((option) =>
    //           otherVariants.find(
    //             (other) => other.option3 === option.value && !other.available
    //           )
    //         )
    //         ?.forEach((el) => el.parentElement.classList.add('disabled'))
    //   }
    // }
    
    updateVariantClasses()

    const chosenVariant = getVariantFromVariantsAndOptions(
      productData.variants,
      currentOptions
    )

    if(!chosenVariant) {
      // auto change to available variant
      for (const select of optionFields) {
        const currentOptions = getCurrentSelectedOptionsFromFields(fields)
        currentOptions[parseInt(select.dataset.position)] = select.value
  
        if(parseInt(select.dataset.position) !== inputPosition) {
          // do not check own opt group
          const variant = getVariantFromVariantsAndOptions(
            productData.variants,
            currentOptions
          )

          if(getAvailabilityFlag(variant) === 'available') {
            if (select.tagName.toLowerCase() === 'input') {
              select.click() // auto change for radio inputs
            } 
            break
          } else if (select.tagName.toLowerCase() === 'select') {
            if (select instanceof HTMLSelectElement) {
              for (const option of select.options) { // grabs all options in the select element
                if (!option.classList.contains('hide')) { // first available option (that isn't hidden)
                  option.selected = true
                  fieldsChange2(select, parseInt(select.dataset.position)) // recursive to force the returned undefined variant to update using the new selected option
                  break
                }
              }
            }
            break
          }
        }
      }
    }
  }

  optionFields.forEach((select) => {
    select.addEventListener('change', (e) => {
      const inputPosition: number = parseInt(
        (<HTMLInputElement | HTMLSelectElement>e.target).dataset.position
      )
      const target = <HTMLInputElement | HTMLSelectElement>e.target
      fieldsChange2(target, inputPosition)
    })
  })

  updateVariantClasses()

  window['blubolt'].selectZoomImage = (imagePosition: number) => {
    const thumbnail = document.querySelector(
      `[data-product-thumbnail-index="${imagePosition}"]`
    )
    if (!thumbnail) {
      console.error('could not find the image selected', imagePosition)
      return
    }
    onProductThumbnailClick(thumbnail)()
  }

  onFieldsChange(fields)
  
  const variantOptionsToggle = () => {
    const toggleEl = document.querySelector('[data-see-more]') as HTMLElement;
    const hiddenEls = document.querySelectorAll('[data-option-hidden]') as NodeListOf<HTMLElement>

    if (!toggleEl) return;
    
    const seeMoreMsg = toggleEl.dataset.seeMore
    const seeLessMsg = toggleEl.dataset.seeLess

    for(const el of hiddenEls) {
      const elRadio = el.querySelector('.variant-select-input') as HTMLInputElement;
      if (elRadio.checked) {
        // We need to check if the customer is viewing a variant that is supposed to be hidden
        // If they are, we unhide all the elements which may have already been hidden and toggle the show more/less lang/classes
        // hiddenEls.forEach(el => {
        //   el.classList.remove('hide');
        // });
        toggleEl.innerHTML = seeLessMsg
        toggleEl.classList.remove('product-input-radio-toggle--more')
        toggleEl.classList.add('product-input-radio-toggle--less')
        break;
      } else {
        el.classList.add('hide')
      }
    }

    toggleEl.addEventListener('click', () => {
      if (toggleEl.innerHTML.includes(seeMoreMsg)) {
        // hiddenEls.forEach(el => {
        //   el.classList.remove('hide');
        // })
        toggleEl.innerHTML = seeLessMsg
        toggleEl.classList.remove('product-input-radio-toggle--more')
        toggleEl.classList.add('product-input-radio-toggle--less')
      } else {
        // hiddenEls.forEach(el => {
        //   el.classList.add('hide');
        // })
        toggleEl.innerHTML = seeMoreMsg
        toggleEl.classList.add('product-input-radio-toggle--more')
        toggleEl.classList.remove('product-input-radio-toggle--less')
      }
    })
  }

  variantOptionsToggle();

  initQuantitySelector();

  updateVariantClasses();
}

